import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../services/api';

export const DISPLAY_CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'JPY', 'SEK', 'NOK', 'DKK', 'CAD', 'AUD'] as const;
export type DisplayCurrency = typeof DISPLAY_CURRENCIES[number];

interface CurrencyContextType {
  displayCurrency: string;
  setDisplayCurrency: (c: string) => void;
  formatAmount: (usdValue: number, isPrivate: boolean, opts?: { minimumFractionDigits?: number; maximumFractionDigits?: number }) => string;
  formatAmountCompact: (usdValue: number, isPrivate: boolean) => string;
}

const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

const CURRENCY_KEY = 'aumora_display_currency';
const DEFAULT_CURRENCY = 'EUR';

const getInitialCurrency = (): string => {
  try {
    return localStorage.getItem(CURRENCY_KEY) || DEFAULT_CURRENCY;
  } catch {
    return DEFAULT_CURRENCY;
  }
};

export const CurrencyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [displayCurrency, setDisplayCurrencyState] = useState(getInitialCurrency);
  const [rates, setRates] = useState<Record<string, number>>({ USD: 1.0 });

  useEffect(() => {
    api.get('/exchange_rates').then(r => setRates(r.data)).catch(() => {});
  }, []);

  const setDisplayCurrency = (c: string) => {
    setDisplayCurrencyState(c);
    try { localStorage.setItem(CURRENCY_KEY, c); } catch {}
  };

  const formatAmount = useCallback((
    usdValue: number,
    isPrivate: boolean,
    opts: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {}
  ): string => {
    if (isPrivate) return '***';
    const rate = rates[displayCurrency] ?? 1.0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: opts.minimumFractionDigits ?? 2,
      maximumFractionDigits: opts.maximumFractionDigits ?? 2,
    }).format(usdValue * rate);
  }, [displayCurrency, rates]);

  const formatAmountCompact = useCallback((usdValue: number, isPrivate: boolean): string => {
    if (isPrivate) return '***';
    const rate = rates[displayCurrency] ?? 1.0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      notation: 'compact',
      maximumFractionDigits: 1,
    } as Intl.NumberFormatOptions).format(usdValue * rate);
  }, [displayCurrency, rates]);

  return (
    <CurrencyContext.Provider value={{ displayCurrency, setDisplayCurrency, formatAmount, formatAmountCompact }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = (): CurrencyContextType => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within a CurrencyProvider');
  return ctx;
};
